import { useCallback, useEffect, useRef } from 'react'
import { getSocket } from '../../socket/client.js'

const STOP_AFTER_MS = 2500

/**
 * Emits throttled `channel:typing` start/stop for the agent's own composer.
 * `signalTyping` sends `start` once, then `stop` after 2.5s of silence;
 * `stopTyping` sends `stop` immediately (after send / chat switch / unmount).
 */
export function useTypingSignal(channel: 'whatsapp' | 'facebook' | 'instagram', chatId: string | null): {
  signalTyping: () => void
  stopTyping: () => void
} {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const activeChatRef = useRef<string | null>(null)

  const emit = useCallback((id: string, state: 'start' | 'stop'): void => {
    try {
      getSocket().emit('channel:typing', { channel, chatId: id, state })
    } catch {
      /* best-effort */
    }
  }, [channel])

  const stopTyping = useCallback((): void => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
      if (activeChatRef.current) emit(activeChatRef.current, 'stop')
    }
  }, [emit])

  const signalTyping = useCallback((): void => {
    if (!chatId) return
    if (!timerRef.current) {
      activeChatRef.current = chatId
      emit(chatId, 'start')
    } else {
      clearTimeout(timerRef.current)
    }
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      emit(chatId, 'stop')
    }, STOP_AFTER_MS)
  }, [chatId, emit])

  // Chat switch / unmount: close out any open typing state for the old chat.
  useEffect(() => stopTyping, [chatId, stopTyping])

  return { signalTyping, stopTyping }
}
